import React, { useState, useEffect } from 'react';
import { API_URL } from '../../services/api';
import { getFullImageUrl } from '../../config/imageConfig';
import {
  Container,
  Typography,
  Box,
  Button,
  Alert,
  CircularProgress
} from '@mui/material';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import axios from 'axios';

const AdminSettings: React.FC = () => {
  const [scheduleImage, setScheduleImage] = useState<string>('');
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string>('');
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState<{
    type: 'success' | 'error' | 'info';
    text: string;
  } | null>(null);

  useEffect(() => {
    fetchCurrentImage();
  }, []);

  const fetchCurrentImage = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/settings/schedule_image`);
      if (response.data.success && response.data.setting) {
        const imageUrl = response.data.setting.setting_value;
        setScheduleImage(imageUrl);
      }
    } catch (error) {
      console.error('Error fetching current image:', error);
      setMessage({
        type: 'error',
        text: 'Error al cargar la configuración actual'
      });
    }
  };

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.files && event.target.files[0]) {
      const file = event.target.files[0];

      // Validar tipo de archivo
      if (!file.type.startsWith('image/')) {
        setMessage({
          type: 'error',
          text: 'Por favor selecciona un archivo de imagen válido'
        });
        return;
      }

      // Validar tamaño (máximo 10MB)
      if (file.size > 10 * 1024 * 1024) {
        setMessage({
          type: 'error',
          text: 'La imagen no debe superar los 10MB'
        });
        return;
      }

      setSelectedFile(file);

      // Crear preview
      const reader = new FileReader();
      reader.onloadend = () => {
        setPreviewUrl(reader.result as string);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleUpload = async () => {
    if (!selectedFile) {
      setMessage({
        type: 'error',
        text: 'Por favor selecciona una imagen primero'
      });
      return;
    }

    setLoading(true);
    setMessage(null);

    const formData = new FormData();
    formData.append('image', selectedFile);

    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(
        `${API_URL}/api/settings/schedule-image`,
        formData,
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'multipart/form-data'
          }
        }
      );

      if (response.data.success) {
        setMessage({
          type: 'success',
          text: 'Imagen de la parrilla actualizada correctamente'
        });
        // Actualizar la imagen mostrada
        setScheduleImage(response.data.imagePath);
        setSelectedFile(null);
        setPreviewUrl('');
        // Recargar la imagen actual para asegurar que se muestre
        await fetchCurrentImage();
      } else {
        throw new Error(response.data.message || 'Error al actualizar la imagen');
      }
    } catch (error: any) {
      console.error('Error uploading image:', error);
      setMessage({
        type: 'error',
        text: error.response?.data?.message || 'Error al subir la imagen'
      });
    } finally {
      setLoading(false);
    }
  };

  const handleCancelSelection = () => {
    setSelectedFile(null);
    setPreviewUrl('');
    setMessage(null);
  };

  return (
    <Container maxWidth="lg">
      <Box sx={{ mb: 4 }}>
        <Typography variant="h4" sx={{ fontWeight: 700, mb: 2 }}>
          Configuración del Sitio
        </Typography>
        <Typography variant="body1" color="text.secondary">
          Administra las configuraciones generales del sitio web
        </Typography>
      </Box>

      {message && (
        <Alert
          severity={message.type}
          onClose={() => setMessage(null)}
          sx={{ mb: 3 }}
        >
          {message.text}
        </Alert>
      )}

      <Box sx={{ mt: 3 }}>
        <Box sx={{ mb: 3 }}>
          <Typography variant="h5" sx={{ fontWeight: 600, mb: 2 }}>
            Imagen de Parrilla de Programación
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            Esta imagen se muestra en la página de Programación bajo las pestañas de días.
            Se recomienda usar una imagen en alta resolución para mejor visualización.
          </Typography>
        </Box>

        <Box sx={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
            {/* Imagen actual */}
            <Box sx={{ flex: 1, minWidth: 300 }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2 }}>
                Imagen Actual
              </Typography>
              {scheduleImage ? (
                <Box>
                  <img
                    src={getFullImageUrl(scheduleImage)}
                    alt="Parrilla actual"
                    style={{
                      width: '100%',
                      maxWidth: '500px',
                      height: 'auto',
                      border: '1px solid #e0e0e0',
                      borderRadius: '4px'
                    }}
                  />
                  <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                    {scheduleImage}
                  </Typography>
                </Box>
              ) : (
                <Box
                  sx={{
                    p: 4,
                    textAlign: 'center',
                    backgroundColor: 'rgba(255, 255, 255, 0.05)',
                    border: '1px solid rgba(255, 255, 255, 0.1)',
                    borderRadius: 1
                  }}
                >
                  <Typography sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>
                    No hay imagen configurada
                  </Typography>
                </Box>
              )}
            </Box>

            {/* Nueva imagen */}
            <Box sx={{ flex: 1, minWidth: 300 }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2 }}>
                Cambiar Imagen
              </Typography>

              {!selectedFile ? (
                <Box
                  component="label"
                  sx={{
                    display: 'block',
                    p: 4,
                    textAlign: 'center',
                    border: '2px dashed rgba(255, 255, 255, 0.2)',
                    borderRadius: 1,
                    backgroundColor: 'rgba(255, 255, 255, 0.02)',
                    cursor: 'pointer',
                    transition: 'all 0.3s',
                    '&:hover': {
                      borderColor: 'primary.main',
                      backgroundColor: 'rgba(255, 255, 255, 0.05)'
                    }
                  }}
                >
                  <input
                    type="file"
                    hidden
                    accept="image/*"
                    onChange={handleFileSelect}
                  />
                  <CloudUploadIcon sx={{ fontSize: 48, color: 'rgba(255, 255, 255, 0.5)', mb: 2 }} />
                  <Typography variant="body1" sx={{ mb: 1, color: 'rgba(255, 255, 255, 0.9)' }}>
                    Haz clic para seleccionar una imagen
                  </Typography>
                  <Typography variant="caption" sx={{ color: 'rgba(255, 255, 255, 0.6)' }}>
                    Formatos soportados: JPG, PNG, GIF (máx. 10MB)
                  </Typography>
                </Box>
              ) : (
                <Box>
                  <img
                    src={previewUrl}
                    alt="Vista previa"
                    style={{
                      width: '100%',
                      maxWidth: '500px',
                      height: 'auto',
                      border: '1px solid #e0e0e0',
                      borderRadius: '4px',
                      marginBottom: '16px'
                    }}
                  />
                  <Typography variant="caption" color="text.secondary" gutterBottom sx={{ display: 'block', mb: 2 }}>
                    {selectedFile.name} ({(selectedFile.size / 1024 / 1024).toFixed(2)} MB)
                  </Typography>
                  <Box sx={{ display: 'flex', gap: 2 }}>
                    <Button
                      variant="contained"
                      color="primary"
                      onClick={handleUpload}
                      disabled={loading}
                      startIcon={loading ? <CircularProgress size={20} /> : <CloudUploadIcon />}
                    >
                      {loading ? 'Subiendo...' : 'Subir Imagen'}
                    </Button>
                    <Button
                      variant="outlined"
                      onClick={handleCancelSelection}
                      disabled={loading}
                    >
                      Cancelar
                    </Button>
                  </Box>
                </Box>
              )}
            </Box>
          </Box>

          <Box sx={{ mt: 4, p: 2, backgroundColor: 'rgba(255, 255, 255, 0.05)', borderRadius: 1, border: '1px solid rgba(255, 255, 255, 0.1)' }}>
            <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>
              <strong>Consejo:</strong> Para mejores resultados, usa una imagen con las dimensiones
              adecuadas para mostrar la parrilla de programación completa. La imagen debe ser clara
              y legible cuando se visualice en diferentes dispositivos.
            </Typography>
          </Box>
      </Box>
    </Container>
  );
};

export default AdminSettings;